<template>
  <div
    class="timeline-item"
    :class="{
      'timeline-item-active': isActive,
      'timeline-item-completed': isCompleted,
      'timeline-item-alternating': alternating && index % 2 === 1,
    }"
  >
    <div class="timeline-item-connector" v-if="index > 0"></div>
    <div class="timeline-item-content">
      <div class="timeline-item-icon">
        <component :is="isCompleted ? completedIcon : icon" class="h-4 w-4 text-white" />
      </div>
      <div class="timeline-item-body">
        <div class="timeline-item-header">
          <h3 class="timeline-item-title">
            {{ title }}
            <span
              class="text-[#9197B3] font-normal text-sm"
              v-if="actionLog && actionLog.manual_action"
            >
              <span>Moved by {{ activityUser?.name }}</span>
              <span v-if="actionLog && actionLog.reason">: </span>
              <span v-if="actionLog && actionLog.reason">"{{ actionLog.reason }}"</span>
            </span>
            <span v-if="activityDateTime" class="text-[#9197B3] font-normal text-sm">{{ activityDateTime }}</span>
          </h3>
          <div v-if="attribution" v-html="sanitize(attribution)" class="timeline-item-attribution"></div>
          <template v-if="showImportantBanner">
            <div
              class="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800"
            >
              <div class="flex-shrink-0">
                <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="text-sm">
                <span class="font-semibold">Important:</span>
                <span class="ml-1">No Further Changes Can Be Made At This Stage.</span>
              </div>
            </div>
          </template>
        </div>
        <div class="timeline-item-content-wrapper">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ActionLog, User } from '@/models';
import { sanitize } from '@/utils/sanitize';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import { computed, inject } from 'vue';

interface Props {
  title: string;
  attribution?: string;
  index: number;
  totalItems: number;
  currentStep?: number;
  icon?: any;
  completedIcon?: any;
  alternating?: boolean;
  activityDateTime: string;
  actionLog: ActionLog;
  activityUser: User;
  /** When false, hides the â€œNo further changesâ€� notice (e.g. order activity log list). */
  showImportantBanner?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  attribution: '',
  index: 0,
  totalItems: 1,
  currentStep: undefined,
  icon: undefined,
  completedIcon: CheckCircleIcon,
  alternating: undefined,
  actionLog: undefined,
  activityUser: undefined,
  showImportantBanner: true,
});

// Try to get currentStep and alternating from parent Timeline if not provided directly
const parentCurrentStep = inject('currentStep', 0);
const parentAlternating = inject('alternating', true);

const currentStep = computed(() =>
  props.currentStep !== undefined ? props.currentStep : parentCurrentStep
);
const alternating = computed(() =>
  props.alternating !== undefined ? props.alternating : parentAlternating
);

const isActive = computed(() => props.index === currentStep.value);
const isCompleted = computed(() => props.index < currentStep.value);
</script>

<style scoped lang="scss">
.timeline-item {
  position: relative;
  padding-bottom: 1.5rem;

  &:last-child {
    padding-bottom: 0;
  }

  .timeline-item-connector {
    position: absolute;
    top: 0;
    left: 16px;
    width: 2px;
    height: 100%;
    background-color: #219f84; /* gray-300 */
    z-index: 1;
  }

  .timeline-item-content {
    position: relative;
    display: flex;
    z-index: 2;
  }

  .timeline-item-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 2px solid #219f84; /* gray-300 */
    background-color: #219f84;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .timeline-item-body {
    flex: 1;
  }

  .timeline-item-header {
    margin-bottom: 0.5rem;
  }

  .timeline-item-title {
    font-weight: 600;
    font-size: 1rem;
    color: #111827; /* gray-900 */
    @apply flex items-baseline gap-[20px];
  }

  .timeline-item-attribution {
    font-size: 0.875rem;
    color: #6b7280; /* gray-500 */
    display: block;
    ul {
      list-style: disc !important;
    }
  }

  .timeline-item-content-wrapper {
    background-color: #f9fafb; /* gray-50 */
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #e5e7eb; /* gray-200 */
  }

  &.timeline-item-active {
    .timeline-item-icon {
      border-color: #219f84; /* blue-500 */
    }

    .timeline-item-title {
      color: #219f84; /* blue-500 */
    }
  }

  &.timeline-item-completed {
    .timeline-item-connector {
      background-color: #10b981; /* green-500 */
    }

    .timeline-item-icon {
      border-color: #10b981; /* green-500 */
    }
  }

  &.timeline-item-alternating {
    .timeline-item-content {
      flex-direction: row-reverse;

      .timeline-item-icon {
        margin-right: 0;
        margin-left: 1rem;
      }

      .timeline-item-body {
        text-align: right;
      }
    }

    .timeline-item-connector {
      left: auto;
      right: 20px;
    }
  }
}

@media (max-width: 640px) {
  .timeline-item {
    .timeline-item-icon {
      width: 32px;
      height: 32px;
    }

    .timeline-item-connector {
      left: 16px;
    }

    &.timeline-item-alternating {
      .timeline-item-connector {
        right: 16px;
      }
    }
  }
}
</style>
